"use strict";
/* --------------------------------------------------------------------------------------------
 *  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ------------------------------------------------------------------------------------------ */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.INodeIPCTestLogger = exports.releasedText = exports.acquiredText = void 0;
exports.getMockAcquisitionContext = getMockAcquisitionContext;
exports.getMockAcquisitionWorkerContext = getMockAcquisitionWorkerContext;
exports.getMockAcquisitionWorker = getMockAcquisitionWorker;
exports.getMockUtilityContext = getMockUtilityContext;
exports.getMockAcquireContext = getMockAcquireContext;
exports.wait = wait;
exports.printWithLock = printWithLock;
exports.getDistroInfo = getDistroInfo;
exports.getLinuxSupportedDotnetSDKVersion = getLinuxSupportedDotnetSDKVersion;
exports.getLatestLinuxDotnet = getLatestLinuxDotnet;
const os = require("os");
const DirectoryProviderFactory_1 = require("../../Acquisition/DirectoryProviderFactory");
const NodeIPCMutex_1 = require("../../Utils/NodeIPCMutex");
const LinuxVersionResolver_1 = require("../../Acquisition/LinuxVersionResolver");
const StringConstants_1 = require("../../Acquisition/StringConstants");
const MockObjects_1 = require("../mocks/MockObjects");
const MockWindowDisplayWorker_1 = require("../mocks/MockWindowDisplayWorker");
const standardTimeoutTime = 100000;
function getMockAcquisitionContext(mode, version, timeoutTime = standardTimeoutTime, customEventStream, customContext, arch, directory) {
    var _a;
    const extensionContext = customContext !== null && customContext !== void 0 ? customContext : new MockObjects_1.MockExtensionContext();
    const myEventStream = customEventStream !== null && customEventStream !== void 0 ? customEventStream : new MockObjects_1.MockEventStream();
    const workerContext = {
        storagePath: '',
        extensionState: extensionContext,
        eventStream: myEventStream,
        acquisitionContext: getMockAcquireContext(version, arch, mode),
        installationValidator: new MockObjects_1.MockInstallationValidator(myEventStream),
        timeoutSeconds: timeoutTime,
        proxyUrl: undefined,
        installDirectoryProvider: directory ? directory : (0, DirectoryProviderFactory_1.directoryProviderFactory)(mode, ''),
        isExtensionTelemetryInitiallyEnabled: true,
        allowInvalidPathSetting: (_a = customContext === null || customContext === void 0 ? void 0 : customContext.get('dotnetAcquisitionExtension.allowInvalidPaths')) !== null && _a !== void 0 ? _a : false
    };
    return workerContext;
}
function getMockAcquisitionWorkerContext(acquireContext) {
    const extensionContext = new MockObjects_1.MockExtensionContext();
    const myEventStream = new MockObjects_1.MockEventStream();
    const workerContext = {
        storagePath: '',
        extensionState: extensionContext,
        eventStream: myEventStream,
        acquisitionContext: acquireContext,
        installationValidator: new MockObjects_1.MockInstallationValidator(myEventStream),
        timeoutSeconds: standardTimeoutTime,
        proxyUrl: undefined,
        installDirectoryProvider: (0, DirectoryProviderFactory_1.directoryProviderFactory)(acquireContext.mode, ''),
        isExtensionTelemetryInitiallyEnabled: true,
        allowInvalidPathSetting: false
    };
    return workerContext;
}
function getMockAcquisitionWorker(mockContext) {
    const acquisitionWorker = new MockObjects_1.MockDotnetCoreAcquisitionWorker(getMockUtilityContext(), new MockObjects_1.MockVSCodeExtensionContext());
    return acquisitionWorker;
}
function getMockUtilityContext() {
    const utilityContext = {
        ui: new MockWindowDisplayWorker_1.MockWindowDisplayWorker(),
        vsCodeEnv: new MockObjects_1.MockVSCodeEnvironment()
    };
    return utilityContext;
}
function getMockAcquireContext(nextAcquiringVersion, arch, installMode) {
    const acquireContext = {
        version: nextAcquiringVersion,
        architecture: arch,
        requestingExtensionId: 'test',
        mode: installMode
    };
    return acquireContext;
}
exports.acquiredText = 'Acquired Lock:';
exports.releasedText = 'Released Lock:';
function wait(delayMs) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise(resolve => setTimeout(resolve, delayMs));
    });
}
class INodeIPCTestLogger extends NodeIPCMutex_1.INodeIPCMutexLogger {
    constructor() {
        super(...arguments);
        this.logs = [];
    }
    log(msg) {
        this.logs.push(msg);
    }
}
exports.INodeIPCTestLogger = INodeIPCTestLogger;
function printWithLock(lock_1, msg_1, timeToLive_1, logger_1) {
    return __awaiter(this, arguments, void 0, function* (lock, msg, timeToLive, logger, fn = () => { return Promise.resolve(); }, retryDelayMs = 10) {
        const mutex = new NodeIPCMutex_1.NodeIPCMutex(lock, logger);
        const c = yield mutex.acquire(() => __awaiter(this, void 0, void 0, function* () {
            logger.log(`${exports.acquiredText}${msg}`);
            yield fn();
            yield wait(timeToLive);
            logger.log(`${exports.releasedText}${msg}`);
            yield fn();
            return msg;
        }), 40, timeToLive, msg);
        logger.log(`After release, ${msg} returned c: ${c}`);
    });
}
function getDistroInfo(context) {
    return __awaiter(this, void 0, void 0, function* () {
        if (os.platform() !== 'linux') {
            return { distro: '', version: '' };
        }
        return new LinuxVersionResolver_1.LinuxVersionResolver(context, getMockUtilityContext()).getRunningDistro();
    });
}
/**
 *
 * @param distroInfo The distro and version of the system
 * @returns The built-in distro supported version of the .NET SDK.
 * Only maintaining the microsoft supported versions for now.
 */
function getLinuxSupportedDotnetSDKVersion(context, distroInfo) {
    return __awaiter(this, void 0, void 0, function* () {
        distroInfo !== null && distroInfo !== void 0 ? distroInfo : (distroInfo = yield getDistroInfo(context));
        if (distroInfo.distro === StringConstants_1.UBUNTU_DISTRO_INFO_KEY) {
            if (distroInfo.version < '22.04') {
                return '6.0.100';
            }
            if (distroInfo.version < '22.06') {
                return '9.0.100';
            }
            if (distroInfo.version < '24.04') {
                return '8.0.100';
            }
            else {
                return '8.0.100';
            }
        }
        else if (distroInfo.distro === StringConstants_1.RED_HAT_DISTRO_INFO_KEY) {
            if (distroInfo.version < '8.0') {
                return '7.0.100';
            }
            else {
                return '9.0.100';
            }
        }
        return getLatestLinuxDotnet(); // best effort guess for latest 'dotnet' version atm.
    });
}
function getLatestLinuxDotnet() {
    return '9.0.100';
}
//# sourceMappingURL=TestUtility.js.map